/**
 * One claim's attribution path (reviews #394, #405): how a billed claim reached the
 * person it is credited to, and, month by month, which worklist it sat on while open
 * and whose open pile it counted in.
 *
 * Beside the credit, what Rand's period table (calc.predicted_worklist_periods) expected
 * at billing and who actually worked the claim first, so a reassignment lag reads as a
 * lag rather than an error (docs/research/PREDICTED-WORKLIST-PERIODS.md §5).
 *
 * Everything shown was decided by the model and copied by the build
 * (src/data/attribution.ts); the assignments come from the Worklist assignments
 * artefact, scoped like every other row, so a person the caller may not see is not
 * listed there. The words follow References/MEASURE-RULES.md §12.9.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { usePageAllowed } from '@/access/pageLimit'
import { SidePanel } from './SidePanel'
import {
  RULE_TEXT,
  creditReason,
  expectedVsActual,
  firstWorker,
  pileOf,
  type ClaimAttribution as Attribution,
  type ExpectedPeriod,
} from '@/data/attribution'
import {
  assignmentsLink,
  decodeAssignments,
  monthBounds,
  overlapsDays,
  priorityOn,
  useAssignments,
  type AssignmentRow,
} from '@/data/assignments'
import { monthYear, monthSpan } from '@/format'
import { DataTable, type Column } from './Table'
import type { TrailRun } from '@/data/attribution'

const usDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}/.test(v) ? `${+v.slice(5, 7)}/${+v.slice(8, 10)}/${v.slice(0, 4)}` : v

/** "2026-07-01".."2026-10-31" -> "Jul 1 – Oct 31, 2026"; the year on both ends when they differ. */
export function periodDates(from: string, to: string): string {
  const d = (v: string) => new Date(`${v.slice(0, 10)}T00:00:00`)
  const md = (v: string) => d(v).toLocaleString('en-US', { month: 'short', day: 'numeric' })
  const y = (v: string) => v.slice(0, 4)
  return y(from) === y(to) ? `${md(from)} – ${md(to)}, ${y(to)}` : `${md(from)}, ${y(from)} – ${md(to)}, ${y(to)}`
}

/** The expected list's facility coverage, in words; '' when it covered it or nobody can tell. */
function coverageNote(e: ExpectedPeriod, facility: string, billMonth: string): string {
  if (e.coverage === 'carried')
    return `This list covers ${facility} only through a carried-forward setting: Worklist assignments shows it off the facility after ${usDate(e.lastOn)}, but the matcher still counted it at the end of ${monthYear(billMonth)}.`
  if (e.coverage === 'never')
    return `Worklist assignments never shows this list on ${facility} by the end of ${monthYear(billMonth)}, so the matcher may be reading an old or one-off setting.`
  return ''
}

/** "2026-03".."2026-05" -> "Mar – May 2026". */
const months = (from: string, to: string) => (from === to ? monthYear(from) : monthSpan([from, to]))

function WorklistName({ name, id, month }: { name: string; id: number | null; month?: string }) {
  // Plain text for a page-limited reader without that page (access/pageLimit.tsx).
  const linked = usePageAllowed('/worklist-assignments')
  if (!name) return <span style={{ color: 'var(--ink-3)' }}>none</span>
  if (id === null || !linked) return <>{name}</>
  return (
    <Link to={assignmentsLink(id, month)} className="cell-link" title="Who was assigned to this worklist, on Worklist assignments">
      {name}
    </Link>
  )
}

/** The people on the predicted worklist in the bill month, merged across facilities. */
function assignedIn(
  rows: readonly AssignmentRow[],
  id: number,
  month: string,
  facility: string,
  facilityId: number | null,
) {
  const { from, to } = monthBounds(month)
  const byPerson = new Map<string, { from: string; to: string; atEnd: boolean; here: boolean }>()
  for (const r of rows) {
    if (r.worklistId !== id || !overlapsDays(r, from, to)) continue
    const e = byPerson.get(r.person) ?? { from: r.from, to: r.to, atEnd: false, here: false }
    if (r.from < e.from) e.from = r.from
    if (r.to > e.to) e.to = r.to
    if (r.from <= to && r.to >= to) e.atEnd = true
    // By id where both sides have it: the names differ two times in three.
    if (facilityId !== null && r.facilityId !== null ? r.facilityId === facilityId : r.facility === facility)
      e.here = true
    byPerson.set(r.person, e)
  }
  return [...byPerson].sort((a, b) => Number(b[1].atEnd) - Number(a[1].atEnd) || a[0].localeCompare(b[0]))
}

export function ClaimAttribution({
  claim,
  visit,
  facility,
  facilityId = null,
  onOpenPrimary = null,
  onClose,
}: {
  claim: Attribution
  visit: string
  facility: string
  /** The claim's facility id (claims.json `fid`), for matching assignments. */
  facilityId?: number | null
  /** Opens the primary claim's own path; null when it is not among the caller's rows. */
  onOpenPrimary?: (() => void) | null
  onClose: () => void
}) {
  const { data, error } = useAssignments()
  const withAssignments = usePageAllowed('/worklist-assignments')
  const rows = useMemo(() => (data ? decodeAssignments(data) : []), [data])
  const billMonth = claim.billDate.slice(0, 7)
  const assigned = claim.worklistId !== null && billMonth ? assignedIn(rows, claim.worklistId, billMonth, facility, facilityId) : []
  const before = !!data?.from && billMonth < data.from.slice(0, 7)
  // The predicted worklist's priority at the bill month's end, where the history has it.
  const monthEnd = billMonth ? monthBounds(billMonth).to : ''
  const spans = rows.find((r) => r.worklistId === claim.worklistId && r.priorities.length)?.priorities
  const priorityThen = spans && monthEnd ? priorityOn(spans, monthEnd) : null
  // The model's own reason, where the build read it (co-insurance, from 2026-10-12).
  const reason = creditReason(claim)
  // What the period table expected at billing, and who first actually had it.
  const expected = claim.expected ?? null
  const worker = firstWorker(claim.trail, claim.billDate)
  const verdict = expected && worker ? expectedVsActual(expected, claim.worklist, worker) : ''

  const trailColumns: Column<TrailRun>[] = [
    { key: 'm', header: 'Month ends', width: 130, sort: (r) => r.from, render: (r) => months(r.from, r.to) },
    {
      key: 'wl',
      header: 'Worklist it sat on',
      width: 260,
      sort: (r) => r.worklist,
      render: (r) => (
        <>
          <WorklistName name={r.worklist} id={r.worklistId} month={r.to} />
          {r.system && <span style={{ color: 'var(--ink-3)' }}> · system list</span>}
        </>
      ),
    },
    { key: 'h', header: 'Assigned to', width: 160, sort: (r) => r.holder, render: (r) => r.holder },
    { key: 'p', header: 'Counted in the open pile of', width: 200, sort: (r) => pileOf(r), render: (r) => pileOf(r) },
    {
      key: 'n',
      header: 'Note',
      width: 220,
      sort: (r) => r.rule || null,
      render: (r) =>
        r.rule === 'left-next' ? (
          'They had left: the model credits the next person to hold it'
        ) : r.rule === 'left-kept' ? (
          'They had left, and nobody has held it since'
        ) : (
          <span style={{ color: 'var(--ink-3)' }}>—</span>
        ),
    },
  ]

  return (
    <SidePanel onClose={onClose} width={860} label={`How visit ${visit} was attributed`}>
      <span className="eyebrow">How this claim was attributed</span>
      <h2 className="page-title" style={{ marginTop: 4 }}>
        Visit {visit}
      </h2>
      <p className="body-note" style={{ marginTop: 4 }}>
        {facility} · billed {usDate(claim.billDate)} · credited to {claim.owner}
      </p>

      <h3 className="card-heading" style={{ marginTop: 18 }}>
        Billed: who it is credited to
      </h3>
      <div className="ca-path" data-testid="attribution-billed">
        <div className="ca-step">
          <span className="eyebrow">1 · Predicted worklist</span>
          <div>
            <WorklistName name={claim.worklist} id={claim.worklistId} month={billMonth} />
            <div className="body-note" style={{ margin: '4px 0 0' }}>
              {claim.worklist
                ? `The worklist whose setup (facility, payer type, service type, plan) covered this claim at the end of ${monthYear(billMonth)}. Where two did, the one with the higher priority.`
                : 'The matcher found no worklist covering this claim.'}
              {claim.worklistId !== null &&
                (priorityThen !== null
                  ? ` Its priority on ${usDate(monthEnd)}: ${priorityThen}.`
                  : data?.priorityFrom && monthEnd < data.priorityFrom
                    ? ` The priority history starts ${usDate(data.priorityFrom)}, after this month end.`
                    : '')}
            </div>
          </div>
        </div>
        <div className="ca-arrow">↓</div>
        <div className="ca-step">
          <span className="eyebrow">2 · Assigned then</span>
          <div>
            {claim.worklistId === null ? (
              <span style={{ color: 'var(--ink-3)' }}>
                {claim.worklist ? 'This worklist has no assignment history to show.' : '—'}
              </span>
            ) : !data ? (
              <span style={{ color: 'var(--ink-3)' }}>{error ?? 'Loading assignments…'}</span>
            ) : before ? (
              <span style={{ color: 'var(--ink-3)' }}>
                The assignment history starts {usDate(data.from!)}, after this bill month.
              </span>
            ) : assigned.length === 0 ? (
              <span style={{ color: 'var(--ink-3)' }}>Nobody you can see was assigned to it in {monthYear(billMonth)}.</span>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {assigned.map(([person, a]) => (
                  <li key={person}>
                    {person}: {usDate(a.from)} – {usDate(a.to)}
                    {a.atEnd ? ', on it at the month end' : ', off it before the month end'}
                    {!a.here && ' (not for this facility)'}
                  </li>
                ))}
              </ul>
            )}
            <div className="body-note" style={{ margin: '4px 0 0' }}>
              The matcher’s person: <strong>{claim.predicted || 'nobody'}</strong>
              {claim.worklistId !== null && withAssignments && (
                <>
                  {' · '}
                  <Link to={assignmentsLink(claim.worklistId, billMonth)} className="cell-link">
                    open on Worklist assignments
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="ca-arrow">↓</div>
        <div className="ca-step">
          <span className="eyebrow">3 · Credited to</span>
          <div>
            <strong>{claim.owner}</strong>
            {reason && (
              <div className="body-note" style={{ margin: '4px 0 0' }} data-testid="attribution-coins">
                <strong>Co-insurance.</strong> {reason.before}
                {reason.primary &&
                  (onOpenPrimary ? (
                    <button
                      type="button"
                      className="cell-link mono"
                      data-testid="attribution-primary"
                      title="Open the primary claim's own path"
                      onClick={onOpenPrimary}
                    >
                      {reason.primary}
                    </button>
                  ) : (
                    <span className="mono" title="Not among the claims you can see on this page: billed outside the period, or credited to someone outside your access">
                      {reason.primary}
                    </span>
                  ))}
                {reason.after}
              </div>
            )}
            {claim.rule && !(claim.rule === 'primary' && reason) && (
              <div className="body-note" style={{ margin: '4px 0 0' }}>
                <strong>{RULE_TEXT[claim.rule].short}.</strong> {RULE_TEXT[claim.rule].long}
              </div>
            )}
          </div>
        </div>
      </div>

      {claim.expected !== undefined && (
        <>
          <h3 className="card-heading" style={{ marginTop: 22 }}>
            Expected at billing, and who actually worked it
          </h3>
          <div className="ca-path" data-testid="attribution-expected">
            <div className="ca-step">
              <span className="eyebrow">Expected at billing (period table)</span>
              <div>
                {expected ? (
                  <>
                    <WorklistName name={claim.worklist} id={claim.worklistId} month={billMonth} />
                    {' · '}
                    <strong>{expected.person || 'nobody'}</strong>
                    <div className="body-note" style={{ margin: '4px 0 0' }}>
                      {periodDates(expected.from, expected.to)}
                      {expected.lists !== null &&
                        (expected.lists > 1 ? ` · chosen from ${expected.lists} lists` : ' · the only list that matched')}
                      {expected.priority !== null && ` (priority ${expected.priority} at the start)`}. The period
                      table’s owner for this facility, payer type, service type and plan over those dates, by bill
                      date.
                    </div>
                    {coverageNote(expected, facility, billMonth) && (
                      <div className="body-note" style={{ margin: '4px 0 0' }} data-testid="attribution-carried">
                        <strong>⚑ Not this list’s facility.</strong> {coverageNote(expected, facility, billMonth)}
                      </div>
                    )}
                  </>
                ) : (
                  <span style={{ color: 'var(--ink-3)' }}>
                    {claim.worklist
                      ? 'The period table has no single row for this claim (for a co-insurance claim, usually because its plan is the primary claim’s).'
                      : 'Nothing: the matcher predicted no worklist for this claim.'}
                  </span>
                )}
              </div>
            </div>
            <div className="ca-arrow">↓</div>
            <div className="ca-step">
              <span className="eyebrow">Who actually worked it</span>
              <div>
                {worker ? (
                  <>
                    {worker.noList ? (
                      <span style={{ color: 'var(--ink-3)' }}>No worklist (“{worker.worklist || 'Unassigned'}” in Medevolve)</span>
                    ) : (
                      <WorklistName name={worker.worklist} id={worker.worklistId} month={worker.month} />
                    )}
                    {' · '}
                    <strong>{worker.person}</strong>
                    {verdict && (
                      // Neutral when they differ: a different person is a lag or a reassignment, not an error.
                      <span
                        className={verdict === 'Same as expected' ? 'badge badge--good' : 'badge'}
                        style={{ marginLeft: 8, fontFamily: 'inherit' }}
                        data-testid="attribution-verdict"
                      >
                        {verdict}
                      </span>
                    )}
                    <div className="body-note" style={{ margin: '4px 0 0' }}>
                      {worker.noList
                        ? 'The first person who held it after billing, assigned to them with no worklist: with them by the end of '
                        : 'The first real worklist with a person it sat on after billing: there by the end of '}
                      {monthYear(worker.month)}
                      {worker.days !== null && `, after ${worker.days} ${worker.days === 1 ? 'day' : 'days'} at most`}.
                      Most claims wait about a month on a system list before a person gets them.
                    </div>
                  </>
                ) : (
                  <span style={{ color: 'var(--ink-3)' }}>
                    {claim.trail.length
                      ? 'Not on a person’s worklist at any month end since billing.'
                      : 'Not open at any month end since billing, so no worklist to show.'}
                  </span>
                )}
              </div>
            </div>
          </div>
          <p className="body-note" style={{ marginTop: 8 }}>
            The credit above is the model’s. This compares the period table’s expected owner with the first person who
            actually had the claim. They differ when a facility was just reassigned (the claim often stays with the
            previous owner) or when the list no longer covers the facility.
          </p>
        </>
      )}

      <h3 className="card-heading" style={{ marginTop: 22 }}>
        Open: which worklist it sat on, month by month
      </h3>
      {claim.trail.length === 0 ? (
        <p className="body-note" data-testid="attribution-no-trail">
          Not open at any month end in the period: it was resolved in the month it was
          billed, or before the first month end the app reads.
        </p>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 8 }} data-testid="attribution-trail">
          <DataTable storageKey="claim-attribution.trail" sortParam="trail.sort" rows={claim.trail} columns={trailColumns} rowKey={(r) => r.from} initialSort="m" initialDir="asc" />
        </div>
      )}
      <p className="body-note" style={{ marginTop: 10 }}>
        At each month end the claim counts in the open pile of the person it was assigned
        to in Medevolve then (the last assignment on or before that day), unless the
        worklist is a system list, which is nobody’s pile. That is the backlog on the
        person pages. It is the real assignment, not the prediction above, so the two can
        name different people.
      </p>
    </SidePanel>
  )
}
